import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormModule } from '@rogers/cdk/form';
import { Subscription, fromEvent } from 'rxjs';
import {
  CreditCardNumberOutput,
  CreditCardType,
  CardTypeConstant,
  CardType,
  SemafoneRequestParams,
  SemafoneResponseStatus,
  CreditCardNumberInputFormData,
} from './models/credit-card-number-type';
import { SemafoneEventResponse } from './models/semafone-event.response';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

@Component({
  standalone: true,
  selector: 'ucx-credit-card-number',
  templateUrl: './credit-card-number.component.html',
  styleUrls: ['./credit-card-number.component.scss'],
  imports: [ReactiveFormsModule, FormModule, CommonModule],
})
export class CreditCardNumberComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() creditCardNumberFormData: CreditCardNumberInputFormData =
    {} as CreditCardNumberInputFormData;
  @Input() semafoneConfig: SemafoneRequestParams = {} as SemafoneRequestParams;
  @Input() creditCardNumberform: FormGroup;

  @ViewChild('semafoneIframe') iframe: ElementRef = {} as ElementRef;

  creditCardNumberOutputInfo: CreditCardNumberOutput | null =
    {} as CreditCardNumberOutput;
  subscription: Subscription;
  receiveNewMessage$ = fromEvent<MessageEvent>(window, 'message');
  creditCardType: CardType | undefined;
  securityCodeLimit = 0;
  showError = false;

  get creditCardNumber() {
    return this.creditCardNumberform.get('creditCardNumber');
  }

  ngOnInit(): void {
    this.creditCardNumberform.addControl('creditCardNumber', new FormControl());
  }

  ngAfterViewInit(): void {
    if (this.iframe) {
      this.iframe.nativeElement.src = this.getIframeUrl('');
    }
    this.processCardResponse();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  processCardResponse(): void {
    this.subscription = this.receiveNewMessage$.subscribe((value) => {
      const messageFromSemafone: SemafoneEventResponse = value?.data
        .toString()
        .split('&')
        .map((el) => el.split('='))
        .reduce((acc, cur) => {
          acc[cur[0]] = cur[1];
          return acc;
        }, {});

      if (
        !messageFromSemafone ||
        (messageFromSemafone.semafoneState === SemafoneResponseStatus.Unknown &&
          messageFromSemafone.semafoneValidationState ===
            SemafoneResponseStatus.Unknown)
      ) {
        return;
      }

      if (messageFromSemafone.semafoneValidationErrorCode) {
        if (this.creditCardNumberOutputInfo.creditCardType) {
          this.creditCardType = null;
        }
        if (this.creditCardNumberOutputInfo?.token?.length) {
          this.creditCardNumberOutputInfo.token = null;
        }
        this.creditCardNumber?.setErrors({
          incorrect: true,
        });
        this.showError = true;
      }

      if (messageFromSemafone.semafoneState === SemafoneResponseStatus.Active) {
        this.creditCardNumber?.setErrors({
          incorrect: true,
        });
        return;
      }
      if (
        messageFromSemafone.semafoneResponseCode ===
          SemafoneResponseStatus.Success ||
        messageFromSemafone.serviceResponseCode === '100'
      ) {
        this.showError = false;
        this.creditCardNumber?.setErrors(null);
        this.semafoneConfig.isPanResetOnInitialFocus = true;
        this.iframe.nativeElement.src = this.getIframeUrl(
          messageFromSemafone.maskedPan
        );
        this.createResponse(messageFromSemafone);
        this.creditCardNumber.setValue(this.creditCardNumberOutputInfo);
      }
    });
  }

  private getIframeUrl(maskedCardNumber?: string): string {
    return `${
      this.semafoneConfig.semafoneHost
    }/semafone/service/capture/getSemafoneIMEcommerceFragment.html?transactionReference=1234567890&semafoneMode=DISABLED&gatewayId=MONERIS&methodId=TOKENISE&ppUserId=${
      this.semafoneConfig.userId
    }&ppPassword=${
      this.semafoneConfig.semafonePassword
    }&tenantId=RCI&clientId=9&accountId=${
      this.semafoneConfig.accountId
    }&principle=${this.semafoneConfig.principle}&licenseCode=${
      this.semafoneConfig.semafoneLicCode
    }&responseType=web&transactionType=GetTokenPan&maskedPan=${maskedCardNumber}&isPanFocusedOnInitialise=false&${
      this.semafoneConfig.isPanResetOnInitialFocus || false
    }&elementFontFamily=Arial&elementFontSize=24&elementHeight=48&elementMaxLength=16&elementWidth=2000&responseURL=${
      this.semafoneConfig.semafoneHost
    }/semafone/service/capture/getSemafoneIMResultsEcommerceFragment.html#${encodeURIComponent(
      document.location.href
    )}`;
  }

  createResponse(value?: SemafoneEventResponse): void {
    this.creditCardNumberOutputInfo.token = value?.token;
    this.creditCardNumberOutputInfo.creditCardType = value?.cardType;
    this.setCreditCardType(this.creditCardNumberOutputInfo.creditCardType);
  }

  private setCreditCardType(creditCardType: CreditCardType): void {
    switch (creditCardType) {
      case CreditCardType.V:
      case CreditCardType.VISA:
        this.creditCardType = CardTypeConstant.VISA;
        this.creditCardNumberOutputInfo.securityCodeLimit = 3;
        break;
      case CreditCardType.M:
      case CreditCardType.MASTER:
      case CreditCardType.MASTERCARD:
        this.creditCardNumberOutputInfo.securityCodeLimit = 3;
        this.creditCardType = CardTypeConstant.MASTERCARD;
        break;
      case CreditCardType.A:
      case CreditCardType.AMEX:
        this.creditCardType = CardTypeConstant.AMEX;
        this.creditCardNumberOutputInfo.securityCodeLimit = 4;
        break;
      default:
        this.creditCardType = null;
        this.creditCardNumberOutputInfo.securityCodeLimit = 4;
    }
  }
}
